import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ShoppingBag, Printer, FileIcon, Search, ArrowRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const categories = ['all', 'design', 'digital', 'template', 'course', 'ebook', 'art', 'music', 'software', 'other'];

const getImageSrc = (img) => {
  if (!img) return null;
  if (img.startsWith('/api/')) return `${BACKEND_URL}${img}`;
  if (img.startsWith('data:')) return img;
  return `data:image/jpeg;base64,${img}`;
};

export default function PublicMarketplacePage() {
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = filter !== 'all' ? `?category=${filter}` : '';
        const res = await api.get(`/products${params}`);
        setProducts(res.data || []);
      } catch {} finally { setLoading(false); }
    };
    load();
  }, [filter]);

  const filtered = search
    ? products.filter(p =>
        p.title?.toLowerCase().includes(search.toLowerCase()) ||
        p.description?.toLowerCase().includes(search.toLowerCase())
      )
    : products;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'PushPro Marketplace',
    description: 'Browse and buy premium digital products created by talented creators on PushPro.',
    url: `${window.location.origin}/marketplace`,
  };

  return (
    <div className="min-h-screen bg-background font-sans">
      <SEOHead
        title="Marketplace"
        description="Browse premium digital products on PushPro. Wallpapers, templates, ebooks, courses, and more from independent creators."
        url={`${window.location.origin}/marketplace`}
        schema={schema}
      />

      {/* Nav */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-black/5">
        <div className="max-w-6xl mx-auto flex items-center justify-between h-14 px-4 md:px-8">
          <Link to="/" className="font-serif text-lg tracking-tight">
            <span className="text-obsidian">Push</span>
            <span className="text-amber-600 font-bold">Pro</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/creators">
              <Button variant="ghost" className="text-xs h-9 px-4 hidden md:flex">Creators</Button>
            </Link>
            <Link to="/auth">
              <Button className="rounded-sm text-xs uppercase tracking-wider h-9 px-6 bg-obsidian hover:bg-amber-600 transition-colors duration-300">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="font-serif text-4xl sm:text-5xl text-obsidian mb-4" data-testid="marketplace-page-title">
            Marketplace
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto font-light">
            Premium digital products from independent creators worldwide.
          </p>
        </div>

        {/* Search & Filters */}
        <div className="mb-8 space-y-4">
          <div className="relative max-w-md mx-auto">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 rounded-full border-border/60 focus:border-amber-400"
              data-testid="marketplace-search"
            />
          </div>
          <div className="flex gap-2 justify-center flex-wrap" data-testid="marketplace-filters">
            {categories.map(c => (
              <Button
                key={c}
                variant={filter === c ? 'default' : 'outline'}
                size="sm"
                className={`rounded-full text-xs capitalize ${filter === c ? 'bg-obsidian text-white' : 'hover:border-amber-400'}`}
                onClick={() => setFilter(c)}
                data-testid={`public-filter-${c}`}
              >
                {c}
              </Button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-amber-500 border-t-transparent" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingBag size={48} className="mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground">No products found.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="public-products-grid">
            {filtered.map((p) => {
              const isPod = p.is_design_product || p.category === 'design';
              const imgSrc = getImageSrc(p.images?.[0]);

              return (
                <Link
                  key={p.id}
                  to={`/products/${p.id}`}
                  className="group bg-white border border-border/40 rounded-2xl overflow-hidden hover:border-amber-300/50 hover:shadow-xl hover:shadow-amber-100/30 transition-all duration-500"
                  data-testid={`public-product-card-${p.id}`}
                >
                  <div className="aspect-square bg-stone-50 relative overflow-hidden">
                    {imgSrc ? (
                      <img src={imgSrc} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ShoppingBag size={32} className="text-muted-foreground/20" />
                      </div>
                    )}
                    <div className="absolute bottom-3 left-3 flex gap-1.5">
                      <Badge className="bg-white/90 text-obsidian rounded-full text-[10px] uppercase backdrop-blur-sm">{p.category}</Badge>
                      {isPod && (
                        <Badge className="bg-purple-500/90 text-white rounded-full text-[10px] backdrop-blur-sm">
                          <Printer size={8} className="mr-1" /> POD
                        </Badge>
                      )}
                      {!isPod && (p.file_count || 0) > 0 && (
                        <Badge className="bg-obsidian/80 text-white rounded-full text-[10px] backdrop-blur-sm">
                          <FileIcon size={8} className="mr-1" /> {p.file_count} files
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-serif text-sm mb-1 group-hover:text-amber-700 transition-colors truncate">{p.title}</h3>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground truncate">{p.seller_name}</span>
                      <span className="text-sm font-semibold text-amber-600">{'\u20AC'}{p.price?.toFixed(2)}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50 py-16 mt-8">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-serif text-2xl md:text-3xl mb-3 text-obsidian">Ready to sell your creations?</h2>
          <p className="text-muted-foreground mb-6 font-light">Join PushPro and start earning from your digital products today.</p>
          <Link to="/auth">
            <Button className="rounded-full h-12 px-10 bg-obsidian hover:bg-amber-600 transition-colors font-medium">
              Start Selling <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/40 py-8 bg-white">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground">PushPro. Premium creator marketplace.</p>
          <div className="flex items-center gap-6">
            <Link to="/marketplace" className="text-xs text-muted-foreground hover:text-amber-600 transition-colors">Marketplace</Link>
            <Link to="/creators" className="text-xs text-muted-foreground hover:text-amber-600 transition-colors">Creators</Link>
            <Link to="/auth" className="text-xs text-muted-foreground hover:text-amber-600 transition-colors">Sign In</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
